import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/context/AuthContext';

export interface NoteType {
  id: string;
  value: string;
}

export interface NoteAuthor {
  id: string;
  name: string;
  profileImageUrl: string | null;
}

export interface Note {
  id: string;
  noteText: string;
  noteType: NoteType | null;
  isPrivate: boolean;
  author: NoteAuthor;
  createdAt: string;
  editedAt: string | null;
  isOwnNote: boolean;
}

export interface NotesResponse {
  notes: Note[];
  total: number;
  hasMore: boolean;
}

interface NotesApiResponse {
  success: boolean;
  data: NotesResponse;
}

interface NoteApiResponse {
  success: boolean;
  data: Note;
}

export interface CreateNoteInput {
  groupId: string;
  noteText: string;
  noteTypeId?: string;
  isPrivate?: boolean;
}

export interface UpdateNoteInput {
  noteText?: string;
  noteTypeId?: string;
  isPrivate?: boolean;
}

export function useMemberNotes(
  userId: string | undefined,
  groupId: string | undefined
) {
  const { isSwitchingOrg } = useAuth();

  return useQuery({
    queryKey: ['notes', userId, groupId],
    queryFn: async (): Promise<NotesResponse> => {
      const params = new URLSearchParams();
      params.append('groupId', groupId!);
      params.append('limit', '50');
      const response = await api.get<NotesApiResponse>(
        `/roster/${userId}/notes?${params.toString()}`
      );
      return response.data;
    },
    enabled: !!userId && !!groupId && !isSwitchingOrg,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      input,
    }: {
      userId: string;
      input: CreateNoteInput;
    }) => {
      const response = await api.post<NoteApiResponse>(`/roster/${userId}/notes`, input);
      return response.data;
    },
    onSuccess: (_note, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['notes', variables.userId, variables.input.groupId],
      });
    },
  });
}

export function useUpdateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      noteId,
      input,
    }: {
      noteId: string;
      input: UpdateNoteInput;
    }) => {
      const response = await api.put<NoteApiResponse>(`/notes/${noteId}`, input);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}

export function useDeleteNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      await api.delete(`/notes/${noteId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}
